import { useState } from 'react'
import { Box, Container, Typography } from '@mui/material'
import { Bolt, Shield, Star, ArrowForward } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { designSpecs } from '../../theme/designSpecs'
import Button from '../common/Button'
import AuthModal from '../auth/AuthModal'
import BeforeAfterSlider from './BeforeAfterSlider'

const HeroSection = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [authModalOpen, setAuthModalOpen] = useState(false)

  const prompt =
    'Place this mug on a rustic wooden table with an open book, eyeglasses, and morning sunlight streaming through the window'

  const handleGenerate = () => {
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      setAuthModalOpen(true)
    }
  }

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, lg: 9 },
        position: 'relative',
        overflow: 'hidden',
        background: designSpecs.gradients.background,
      }}
    >
      {/* Background decorations */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(147, 51, 234, 0.05) 50%, transparent 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 80,
          right: 80,
          width: 288,
          height: 288,
          bgcolor: 'primary.main',
          opacity: 0.1,
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 80,
          left: 80,
          width: 384,
          height: 384,
          bgcolor: 'secondary.main',
          opacity: 0.1,
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10 }}>
        {/* Auth Modal */}
        <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} defaultMode="signup" />
        {/* Badge */}

        {/* Hero Title */}
        <Typography
          variant="h1"
          sx={{
            textAlign: 'center',
            mb: 3,
            color: 'grey.900',
          }}
        >
          Transform Your Product Photos with{' '}
          <Box
            component="span"
            sx={{
              background: designSpecs.gradients.primary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            AI
          </Box>
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            mb: 7,
            color: 'grey.600',
            fontWeight: 400,
            fontSize: { xs: designSpecs.typography.sizes.xl, lg: designSpecs.typography.sizes['2xl'] },
          }}
        >
          Upload, describe, generate. Get professional variations instantly
        </Typography>

        {/* Before/After Comparison */}
        <Box sx={{ mb: 4 }}>
          <BeforeAfterSlider prompt={prompt} />
        </Box>

        {/* Generate Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleGenerate}
          sx={{
            background: designSpecs.gradients.primary,
            py: 2.5,
            fontSize: designSpecs.typography.sizes.xl,
            fontWeight: 700,
            boxShadow: '0 20px 25px -5px rgba(99, 102, 241, 0.4)',
            '&:hover': {
              background: designSpecs.gradients.primary,
              boxShadow: '0 25px 50px -12px rgba(99, 102, 241, 0.6)',
              transform: 'translateY(-4px)',
            },
          }}
          endIcon={<ArrowForward />}
        >
          Generate Magic
        </Button>

        {/* Features */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            pt: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Bolt sx={{ color: 'warning.main', fontSize: designSpecs.iconSizes.md }} />
            <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 500 }}>
              Lightning Fast
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield sx={{ color: 'success.main', fontSize: designSpecs.iconSizes.md }} />
            <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 500 }}>
              100% Secure
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ color: 'primary.main', fontSize: designSpecs.iconSizes.md }} />
            <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 500 }}>
              Pro Quality
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default HeroSection
